import { CaseType } from "src/pages/UserDashboard/WorkPackets/Cases/case-types";
import { GridColDef, GridPaginationModel, GridRowModel, GridSortModel } from "@mui/x-data-grid-pro";
import { useEffect, useMemo, useState } from "react";
import { FetchCaseItemsArgs } from "src/pages/UserDashboard/Cases/api/types";
import {
  keepPreviousData,
  QueryClient,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { SortOption } from "src/pages/UserDashboard/WorkPackets/api/workPacketsAPI";
import { updateCase, exportCaseItems } from "src/pages/UserDashboard/Cases/api/casesApi";
import { CaseItemUpdateModel, toServerMappers } from "src/pages/UserDashboard/Cases/mappers/mapLocalToServerCaseItem";
import { columnsMap } from "src/pages/UserDashboard/WorkPackets/Cases/tabs/CaseDetails/CaseItemsGrid/columns";
import { CaseItem } from "src/types/case-items";
import type { GridRowSelectionModel } from "@mui/x-data-grid";
import { useCaseItemsFilters } from "./useCaseItemsFilters";
import toast from "react-hot-toast";
import { useFeatureFlag } from "src/feature-flags";
import { fetchEscalationCaseById, getCaseItemsData } from "src/pages/UserDashboard/Cases/queries";

interface UpdateCaseItemParams {
  caseType: CaseType;
  caseId: string;
  updateParams: Partial<CaseItemUpdateModel>;
  queryClient: QueryClient;
}

const getSortOption = (sortModel: GridSortModel): SortOption | undefined => {
  if (!sortModel[0]) return;
  const { field: key, sort: direction } = sortModel[0];
  if (!direction) return;
  return { key, direction };
};

const updateCaseItemMutationOptions: UseMutationOptions<void, Error, UpdateCaseItemParams> = {
  mutationFn: async ({ caseType, caseId, updateParams }) => void updateCase(caseId, caseType, updateParams),
  mutationKey: ["update-case"],

  onSuccess: async (_data, { caseType, caseId, queryClient }) => {
    await queryClient.invalidateQueries({ queryKey: ["cases", caseType] });
    await queryClient.invalidateQueries({ queryKey: ["cases", caseId] });
  },
};

export const useCaseItemsTable = (caseId: string, caseType: CaseType) => {
  const queryClient = useQueryClient();

  const { applyFilters, filters } = useCaseItemsFilters();

  const [sortingModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });

  const fetchCaseItemsParams = useMemo<FetchCaseItemsArgs>(() => {
    const sorting = getSortOption(sortingModel);
    return { ...paginationModel, sorting, filters };
  }, [paginationModel, sortingModel, filters]);

  const dataQuery = useQuery({
    queryFn: getCaseItemsData,
    queryKey: ["cases", caseType, caseId, "items", fetchCaseItemsParams],
    staleTime: 30000,
    placeholderData: keepPreviousData,
  });
  const [rowSelection, setRowSelection] = useState<GridRowSelectionModel>([]);
  const [excludedFromSelection, setExcludedFromSelection] = useState<string[]>([]);

  const [selectAllMode, setSelectAllMode] = useState<"select-all" | "deselect-all" | null>(null);
  const columns = useMemo<GridColDef[]>(() => columnsMap[caseType], [caseType]);
  const { value: showDeniedReasonColumn } = useFeatureFlag("WORK_PACKET_DENIAL_REASONS", false);

  const filteredColumns = useMemo(
    () => columns.filter(col => (col.field === "deniedReason" ? showDeniedReasonColumn : true)),
    [columns, showDeniedReasonColumn],
  );
  const { mutateAsync } = useMutation(updateCaseItemMutationOptions);

  const { mutateAsync: exportCaseItemsMutation, isPending } = useMutation({
    mutationFn: async () => exportCaseItems(caseId),
    mutationKey: ["export-case-items", caseId],
    onError: () => {
      toast.error("Error exporting Work Packets in this case");
    },
    onSuccess: () => {
      toast.success("Export completed!");
    },
  });

  const processRowUpdate = async (updatedRow: GridRowModel<CaseItem>, originalRow: GridRowModel<CaseItem>) => {
    const updates = Object.fromEntries(
      Object.entries(updatedRow).filter(([key, value]) => value !== originalRow[key as keyof CaseItem]),
    ) as Partial<CaseItem>;
    const serverUpdates = toServerMappers[caseType](originalRow, updates);
    try {
      // todo: handle error - show Snackbar or something?
      await mutateAsync({
        caseType,
        caseId,
        updateParams: serverUpdates,
        queryClient: queryClient,
      });
    } catch (error) {
      console.error("Error updating row", error);
      return originalRow;
    }
    return updatedRow;
  };

  const onDelete = () => {
    queryClient.invalidateQueries({ queryKey: ["cases", caseId] });
  };

  const handleRowSelection = (newSelection: GridRowSelectionModel) => {
    const totalChanges = Math.abs(newSelection.length - rowSelection.length);
    const isBulkSelection = totalChanges > 1;

    if (isBulkSelection) {
      const isSelectAll = newSelection.length > rowSelection.length;
      setSelectAllMode(isSelectAll ? "select-all" : "deselect-all");

      if (!isSelectAll) {
        setSelectAllMode(null);
        setExcludedFromSelection([]);
        setRowSelection([]);
        return;
      }
    }

    if (selectAllMode == "select-all") {
      const excludedIds = dataQuery.data?.items.filter(row => !newSelection.includes(row.id)).map(row => row.id);

      if (excludedIds && excludedIds.length > 0) {
        setExcludedFromSelection(prev => {
          return Array.from(new Set([...prev, ...excludedIds]));
        });
      }
    }

    setRowSelection(newSelection);
  };

  useEffect(() => {
    if (selectAllMode === "select-all") {
      const currentPageRowIds = dataQuery.data?.items.map(row => row.id) || [];

      const newSelection = [
        ...rowSelection,
        ...currentPageRowIds.filter(id => !rowSelection.includes(id) && !excludedFromSelection.includes(id)),
      ];

      // Only update the state if the selection has changed
      if (newSelection.length !== rowSelection.length) {
        setRowSelection(newSelection);
      }
    } else if (selectAllMode === "deselect-all") {
      if (rowSelection.length > 0 || excludedFromSelection.length > 0) {
        setRowSelection([]);
        setExcludedFromSelection([]);
      }
    } else {
      if (excludedFromSelection.length > 0) {
        setExcludedFromSelection([]);
      }
    }
  }, [paginationModel.page, selectAllMode, dataQuery.data?.items, excludedFromSelection, rowSelection]);

  const footerRowSelectedText = (count: number) => {
    const total = dataQuery.data?.total_records || 0;
    const effectiveCount = selectAllMode === "select-all" ? total - excludedFromSelection.length : count;

    return effectiveCount > 1
      ? `${effectiveCount.toLocaleString()} rows selected`
      : `${effectiveCount.toLocaleString()} row selected`;
  };

  const caseQuery = useQuery({
    queryFn: fetchEscalationCaseById,
    queryKey: ["cases", caseType, caseId, "details"],
  });

  return {
    sortingModel,
    setSortModel,
    paginationModel,
    setPaginationModel,
    dataQuery,
    columns: filteredColumns,
    processRowUpdate,
    onDelete,
    handleRowSelection,
    rowSelection,
    selectAllMode: selectAllMode === "select-all",
    excludedFromSelection,
    applyFilters,
    filters,
    localeText: {
      footerRowSelected: footerRowSelectedText,
    },
    setRowSelection,
    handleExport: () => exportCaseItemsMutation(),
    isExporting: isPending,
    fetchCaseItemsParams,
    caseQuery,
  };
};
